/**
 * **The icon a firm picks** — ADR-0045 §5 (C23, Wave 25D, T-4.114).
 *
 * One component, used in three places: the page editor's `icon` fields
 * (`BlockForm`), the header-menu editor (items, dropdowns and their links) and
 * the staff Services form.
 *
 * - **A button** showing the current icon and its label, or *No icon*.
 * - **A popover** (the kit's `Popover`: Escape and outside-click dismiss, focus
 *   returns to the button, nothing is trapped) holding a search box, a
 *   **No icon** option first, and the set grouped as the vocabulary groups it.
 * - **Keyboard-complete.** Focus stays in the search box and the list follows
 *   it (`aria-activedescendant`): the arrow keys, Home and End move, Enter
 *   picks, and **Escape closes without changing anything**.
 *
 * **The set is never restated here.** The icons and their groups come from the
 * vocabulary the screen already read (`GET /api/v1/site/vocabulary`, or
 * `/services/vocabulary`), which serves `app/data/icons.json`; this file only
 * draws them, with the public site's own `SiteIcon`, so the picker shows the
 * drawing the site will. `site/icons.tsx` imports React and Lucide and
 * nothing else, so no template or theme rides into the staff bundle.
 *
 * **No icon is ever defaulted** (ADR-0045 §4): the picker starts from whatever
 * is stored, and *No icon* is `null`.
 */

import { Popover } from '@fiveox/ui'
import { Ban, ChevronDown, Search } from 'lucide-react'
import { createContext, useContext, useId, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react'

import { SiteIcon } from '../../site/icons'
import type { IconChoice, IconGroup, IconSet } from '../lib/api/site'

export interface IconPickerProps {
  /** The stored key, or `null` / `''` for *no icon*. */
  value: string | null | undefined
  /** A key of the set, or `null` for *no icon*. Never called on Escape. */
  onChange: (key: string | null) => void
  icons: IconChoice[]
  groups: IconGroup[]
  /** Names the control and its popover — "Icon" when not given. */
  label?: string
  /** For a `<label htmlFor>` (the kit `Field`'s control id). */
  id?: string
  'aria-describedby'?: string
  disabled?: boolean
}

/** What the trigger reads as: the icon's label, or *No icon*. A stored key the
 *  set no longer knows reads as *No icon* — the site draws nothing for it. */
function currentOf(value: string | null | undefined, icons: IconChoice[]): IconChoice | null {
  return value ? (icons.find((i) => i.key === value) ?? null) : null
}

/** The icons whose label, key or group matches every word of the query. */
export function filterIcons(icons: IconChoice[], groups: IconGroup[], query: string): IconChoice[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return icons
  const groupLabel = new Map(groups.map((g) => [g.key, g.label.toLowerCase()]))
  return icons.filter((icon) => {
    const hay = `${icon.label.toLowerCase()} ${icon.key.toLowerCase()} ${groupLabel.get(icon.group) ?? ''}`
    return words.every((w) => hay.includes(w))
  })
}

export function IconPicker({
  value,
  onChange,
  icons,
  groups,
  label = 'Icon',
  id,
  disabled = false,
  'aria-describedby': describedBy,
}: IconPickerProps) {
  const generated = useId()
  const buttonId = id ?? `icon-picker-${generated}`
  const current = currentOf(value, icons)

  return (
    <Popover
      label={`Choose ${label.toLowerCase()}`}
      className="w-80 max-w-[calc(100vw-2rem)] p-3"
      trigger={(t) => (
        <button
          type="button"
          ref={t.ref}
          id={buttonId}
          aria-haspopup={t['aria-haspopup']}
          aria-expanded={t['aria-expanded']}
          aria-label={`${label}: ${current?.label ?? 'No icon'}`}
          aria-describedby={describedBy}
          onClick={t.onClick}
          disabled={disabled}
          data-icon-picker=""
          data-icon-value={current?.key ?? ''}
          className="inline-flex h-9 min-w-40 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          {current ? (
            <SiteIcon name={current.key} size={16} />
          ) : (
            <Ban size={16} className="text-muted-foreground" aria-hidden="true" />
          )}
          <span className={`flex-1 text-left ${current ? '' : 'text-muted-foreground'}`}>
            {current?.label ?? 'No icon'}
          </span>
          <ChevronDown size={14} className="text-muted-foreground" aria-hidden="true" />
        </button>
      )}
    >
      {(close) => (
        <IconList
          value={current?.key ?? null}
          icons={icons}
          groups={groups}
          label={label}
          onPick={(key) => {
            close()
            document.getElementById(buttonId)?.focus()
            if (key !== (current?.key ?? null)) onChange(key)
          }}
          onCancel={() => {
            close()
            document.getElementById(buttonId)?.focus()
          }}
        />
      )}
    </Popover>
  )
}

/** The popover's body. Mounted each time it opens, so the search starts empty. */
function IconList({
  value,
  icons,
  groups,
  label,
  onPick,
  onCancel,
}: {
  value: string | null
  icons: IconChoice[]
  groups: IconGroup[]
  label: string
  onPick: (key: string | null) => void
  onCancel: () => void
}) {
  const base = useId()
  const [query, setQuery] = useState('')
  const matches = useMemo(() => filterIcons(icons, groups, query), [icons, groups, query])
  // Drawn group by group, in the vocabulary's order; an icon whose group the
  // vocabulary does not name goes last rather than disappearing.
  const sections = useMemo(() => {
    const known = new Set(groups.map((g) => g.key))
    const out = groups
      .map((g) => ({ key: g.key, label: g.label, icons: matches.filter((i) => i.group === g.key) }))
      .filter((s) => s.icons.length > 0)
    const other = matches.filter((i) => !known.has(i.group))
    if (other.length > 0) out.push({ key: '_other', label: 'Other', icons: other })
    return out
  }, [groups, matches])
  // The order the arrows walk: *No icon*, then every icon as drawn.
  const order: (string | null)[] = useMemo(
    () => [null, ...sections.flatMap((s) => s.icons.map((i) => i.key))],
    [sections],
  )
  const [active, setActive] = useState(() => Math.max(0, order.indexOf(value)))
  const activeIndex = Math.min(active, order.length - 1)
  const optionId = (key: string | null) => `${base}-opt-${key ?? 'none'}`

  const move = (next: number) => {
    const clamped = Math.max(0, Math.min(order.length - 1, next))
    setActive(clamped)
    const el = document.getElementById(optionId(order[clamped] ?? null))
    el?.scrollIntoView?.({ block: 'nearest' })
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        move(activeIndex + 1)
        break
      case 'ArrowUp':
        e.preventDefault()
        move(activeIndex - 1)
        break
      case 'Home':
        e.preventDefault()
        move(0)
        break
      case 'End':
        e.preventDefault()
        move(order.length - 1)
        break
      case 'Enter':
        // Never submits a surrounding form: Enter here means *this icon*.
        e.preventDefault()
        onPick(order[activeIndex] ?? null)
        break
      case 'Escape':
        // Closes without a change — and stops here, so a dialog the picker
        // sits in stays open.
        e.preventDefault()
        e.stopPropagation()
        onCancel()
        break
      default:
    }
  }

  const option = (key: string | null, text: string, drawing: ReactNode) => {
    const index = order.indexOf(key)
    const isActive = index === activeIndex
    const selected = key === value
    return (
      <div
        key={key ?? '_none'}
        id={optionId(key)}
        role="option"
        aria-selected={selected}
        data-icon-option={key ?? ''}
        data-active={isActive ? '' : undefined}
        // Focus stays in the search box; a click must not take it away first.
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={() => setActive(index)}
        onClick={() => onPick(key)}
        className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
          isActive ? 'bg-muted text-foreground' : 'text-foreground'
        } ${selected ? 'font-medium' : ''}`}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">{drawing}</span>
        <span className="flex-1 truncate">{text}</span>
        {selected && <span className="text-xs text-muted-foreground">current</span>}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search
          size={14}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          role="combobox"
          aria-label={`Search ${label.toLowerCase()}s`}
          aria-expanded="true"
          aria-controls={`${base}-list`}
          aria-autocomplete="list"
          aria-activedescendant={optionId(order[activeIndex] ?? null)}
          autoFocus
          value={query}
          placeholder="Search icons"
          data-icon-search=""
          onChange={(e) => {
            setQuery(e.target.value)
            setActive(0)
          }}
          onKeyDown={onKeyDown}
          className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div
        id={`${base}-list`}
        role="listbox"
        aria-label={label}
        className="max-h-72 space-y-2 overflow-y-auto"
      >
        {option(null, 'No icon', <Ban size={16} className="text-muted-foreground" aria-hidden="true" />)}
        {sections.map((section) => (
          <div key={section.key} role="group" aria-labelledby={`${base}-g-${section.key}`}>
            <p
              id={`${base}-g-${section.key}`}
              className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              {section.label}
            </p>
            {section.icons.map((icon) => option(icon.key, icon.label, <SiteIcon name={icon.key} size={16} />))}
          </div>
        ))}
        {sections.length === 0 && (
          <p className="px-2 py-1.5 text-sm text-muted-foreground" data-icon-none-match="">
            No icon matches “{query.trim()}”.
          </p>
        )}
      </div>
    </div>
  )
}

/* ── the set, for a form that is drawn from a schema ─────────────────────── */

const IconSetContext = createContext<IconSet | null>(null)

/** Hands the vocabulary's icon set to every `icon` field under it (`BlockForm`). */
export function IconSetProvider({ value, children }: { value: IconSet | null; children: ReactNode }) {
  return <IconSetContext.Provider value={value}>{children}</IconSetContext.Provider>
}

export function useIconSet(): IconSet | null {
  return useContext(IconSetContext)
}
